import { api } from '../app/api/baseApi';

import { useAppDispatch, useTypedSelector } from '../app/store';
import { configureSummaryValues, widgetList } from '../features/dashboardManagement/commonDashboardSlice';
import { setLoadModal } from '@fiji/common/src/features/Loads/loadSlice';
import { setMessageContent } from '../features/common/commonSlice';

type CommandResult = { isSuccess: boolean; hasMessage: boolean };

type ReturnProps = {
    handleDeviceCommands: (args0: any, args1: any) => Promise<CommandResult>;
    handleDeviceLoads: (args0: any, args1: any) => void;
};

export const useDashboardCommonHandlers = (deviceId?: any): ReturnProps => {
    const widgets = useTypedSelector(widgetList);

    const dispatch = useAppDispatch();

    const handleDeviceCommands = (commandData: any, widgetId: any): Promise<CommandResult> => {
        // Capture original config (nested, no isLoading) to restore once the HTTP call settles
        const preCommandConfig = JSON.parse(
            JSON.stringify(widgets?.find((item: any) => item?.id === widgetId)?.config ?? {})
        );
        const selectedWidgetClone = JSON.parse(JSON.stringify(preCommandConfig));
        if (selectedWidgetClone) {
            if (selectedWidgetClone?.secondary?.[0]?.channels) {
                Object.keys(selectedWidgetClone)?.forEach((key: string) => {
                    if (key === 'secondary') {
                        selectedWidgetClone['secondary'] = selectedWidgetClone['secondary']
                            ?.map((item: any) => item.channels)
                            ?.flat(Infinity);
                    }
                });
            }
            const selectedCommandIndex = Array.isArray(selectedWidgetClone['secondary'])
                ? selectedWidgetClone['secondary'].findIndex(
                      (item: any) =>
                          item?.deviceId === commandData?.deviceId && item?.channelId === commandData?.channelId
                  )
                : -1;
            if (typeof selectedCommandIndex === 'number' && selectedCommandIndex !== -1) {
                selectedWidgetClone['secondary'][selectedCommandIndex] = {
                    ...commandData,
                    isLoading: true,
                };
            }
            dispatch(configureSummaryValues({ widgetId: widgetId, config: selectedWidgetClone }));

            return dispatch(
                (api as any)?.endpoints?.['sendDeviceCommand']?.initiate({
                    body: {
                        channelId: commandData?.channelId,
                        tagValue: commandData?.blockValue ? commandData?.blockValue : commandData?.defaultValue,
                    },
                    deviceId: !selectedWidgetClone?.multiDevice ? deviceId : commandData?.deviceId,
                })
            )
                .then((result: any) => {
                    // Always clear isLoading once the command API has settled.
                    // MQTT will still arrive (on supporting platforms) and update channel availability.
                    dispatch(configureSummaryValues({ widgetId: widgetId, config: preCommandConfig }));
                    if (result?.error) {
                        dispatch(
                            setMessageContent({
                                isOpen: true,
                                message: result?.error?.data?.message ?? result?.error?.error,
                            })
                        );
                        return { isSuccess: false, hasMessage: true };
                    } else if (result?.data?.message) {
                        dispatch(setMessageContent({ isOpen: true, message: result?.data?.message }));
                        return { isSuccess: true, hasMessage: true };
                    }
                    return { isSuccess: true, hasMessage: false };
                })
                .catch(() => {
                    // Safety net: if the dispatch chain throws (e.g. network abort, RTK internal
                    // error), restore preCommandConfig so the button spinner is never stuck.
                    dispatch(configureSummaryValues({ widgetId: widgetId, config: preCommandConfig }));
                    return { isSuccess: false, hasMessage: false };
                });
        }
        return Promise.resolve({ isSuccess: false, hasMessage: false });
    };

    const handleDeviceLoads = (loadData: any, widgetId: any): void => {
        dispatch(
            setLoadModal({
                isOpen: true,
                details: loadData,
                command: loadData?.isLoadEnabled ? 'TURN_OFF' : 'TURN_ON',
                widgetId: widgetId,
            })
        );
    };
    return {
        handleDeviceCommands,
        handleDeviceLoads,
    };
};
